import _ from 'lodash';
import React, { Fragment, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';
import InfoDropdownPopover from '../../../../InfoDropdownPopover';
import currencySymbol from '../../../../../helpers/currencySymbol';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { RiInformationLine } from 'react-icons/ri';
import getRentalCarRate from '../../../../../helpers/selectors/getRentalCarRate';
import { CarRate, RootState, User } from '../../../../../types/rentalCar';
import parseRentalCarCharges from '../../parseRentalCarCharges';
const LightTooltip = withStyles((theme) => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[3],
    fontSize: 11,
    maxWidth: '350px',
  },
  arrow: {
    color: theme.palette.common.white,
  },
}))(Tooltip);

const CarRateDetailDropdownPopover: React.FC<any> = ({
  fontSize,
  alignClass,
  popoverAlignClass,
  dropdownLabel,
  doShowMarkupRate,
  priceInfo,
}) => {
  const carRate = getRentalCarRate({ rate: priceInfo, doShowMarkupRate }) as CarRate;
  const user: User = useSelector((state: RootState) => state.user);
  const taxesAndFeesToolTipText =
    user &&
    user.travelAgency &&
    user.travelAgency.settings &&
    user.travelAgency.settings.taxesAndFeesToolTipText;
  const markupAmount =
    carRate && carRate.markupPriceAllDays > 0
      ? +(carRate.markupPriceAllDays - carRate.priceAllDays).toFixed(2)
      : 0;
  const { baseRate, approxTotalRate, taxesAndFees, otherCharges, doShowTaxesAndFees } =
    parseRentalCarCharges(carRate && carRate.vehicleCharges);

  const serviceFeesElement =
    doShowMarkupRate && carRate && markupAmount > 0 ? (
      <>
        <div className="grid grid-cols-2 mt-2">
          <div className="flex gap-1 items-center">
            Service fee
            <LightTooltip title={taxesAndFeesToolTipText} placement="bottom" arrow className="ml-1">
              <div>
                <RiInformationLine size={18} className="cursor-pointer" />
              </div>
            </LightTooltip>
          </div>
          <div className="text-right">
            <NumberFormat
              value={markupAmount}
              prefix={currencySymbol(carRate.currencyCode)}
              displayType="text"
              thousandSeparator
              fixedDecimalScale={true}
              decimalScale={2}
            />
          </div>
        </div>
      </>
    ) : (
      <></>
    );

  const displayCharge = useCallback(
    (vehicleCharge: any) => (
      <div className="grid grid-cols-2 mt-2" key={vehicleCharge.chargeType + vehicleCharge.amount}>
        {vehicleCharge.chargeType}{' '}
        <div className="text-right">
          {vehicleCharge.markupAmount === 0 && vehicleCharge.amount === 0 ? (
            <span>N/A</span>
          ) : (
            <NumberFormat
              value={
                doShowMarkupRate && vehicleCharge.markupAmount > 0
                  ? vehicleCharge.markupAmount
                  : vehicleCharge.amount
              }
              prefix={currencySymbol(carRate.currencyCode)}
              displayType="text"
              thousandSeparator
              fixedDecimalScale={true}
              decimalScale={2}
            />
          )}
        </div>
      </div>
    ),
    [doShowMarkupRate, carRate.currencyCode],
  );

  return (
    <InfoDropdownPopover
      dropdownTextPlaceholder={dropdownLabel}
      fontSize={fontSize}
      alignClass={alignClass}
      popoverAlignClass={popoverAlignClass}
    >
      <div className={`px-5 py-4 text-left font-normal ${fontSize || 'text-sm'}`}>
        <b>Vehicle Charges:</b> <hr className="my-2" />
        {doShowTaxesAndFees ? (
          <>
            {displayCharge(baseRate)}
            {displayCharge({
              chargeType: 'Taxes and Fees',
              amount: taxesAndFees,
              markupAmount: taxesAndFees,
            })}
            {serviceFeesElement}
            <hr className="my-2" />
            {displayCharge(approxTotalRate)}
          </>
        ) : (
          <></>
        )}
        {doShowTaxesAndFees && otherCharges && otherCharges.length > 0 ? (
          <>
            <div className="font-bold mt-5">Other Charges:</div>
            {otherCharges.map((vehicleCharge: any) => displayCharge(vehicleCharge))}
          </>
        ) : (
          <></>
        )}
        {!doShowTaxesAndFees &&
          carRate &&
          carRate.vehicleCharges &&
          carRate.vehicleCharges.map((vehicleCharge: any, index: any) => (
            <Fragment key={vehicleCharge.chargeType + vehicleCharge.amount}>
              {index === carRate.vehicleCharges.length - 1 && serviceFeesElement}
              {displayCharge(vehicleCharge)}
            </Fragment>
          ))}
      </div>
    </InfoDropdownPopover>
  );
};

CarRateDetailDropdownPopover.propTypes = {
  fontSize: PropTypes.string,
  alignClass: PropTypes.string,
  popoverAlignClass: PropTypes.string,
  dropdownLabel: PropTypes.string,
  doShowMarkupRate: PropTypes.bool,
  priceInfo: PropTypes.shape({}).isRequired,
};
CarRateDetailDropdownPopover.defaultProps = {
  fontSize: undefined,
  alignClass: 'justify-end',
  popoverAlignClass: undefined,
  dropdownLabel: 'Price detail',
  doShowMarkupRate: false,
  priceInfo: {},
};
export default memo(CarRateDetailDropdownPopover);
